/**
 * Marketing configuration for public acquisition pages.
 *
 * ⚠️ CLAVES CONGELADAS (migración de slugs RU→ES, 2026-07): las claves de
 * PUBLIC_PAGES (pageId) alimentan eventNames de analítica, atribución `?src=`
 * y claves i18n. Al renombrar una URL se cambia SOLO el campo `path` — la
 * clave se queda como está, aunque ya no coincida con el slug. Renombrar la
 * clave parte las series históricas de analytics_events y da MISSING_MESSAGE.
 *
 * Single source of truth for:
 *   - traffic-source classification (cold / warm / retarget / SEO)
 *   - primary conversion goal (Yandex Metrica reachGoal name)
 *   - CRO variant selection (A/B/C)
 *   - source-aware copy keys
 *
 * Keeps pages composable: landing components read config by pageId.
 */

export type TrafficType = "cold" | "warm" | "retarget" | "seo" | "niche";
export type FunnelRole = "entry" | "first-purchase" | "subscription" | "retention" | "objection";
export type CopyVariant = "A" | "B" | "C";

export interface PageConfig {
  pageId: string;
  path: string;
  titleRu: string;
  trafficTypes: TrafficType[];
  funnelRole: FunnelRole;
  primaryGoal: string;
  secondaryGoals: string[];
  variant: CopyVariant;
  crossSell: string[];
  showPreview: boolean;
  showRegistrationGate: boolean;
  sourceAware: boolean;
}

export const PUBLIC_PAGES: Record<string, PageConfig> = {
  landing: {
    pageId: "landing",
    path: "/",
    titleRu: "Inicio",
    trafficTypes: ["cold", "warm"],
    funnelRole: "entry",
    primaryGoal: "landing_cta_click",
    secondaryGoals: ["landing_preview_start", "landing_scroll_50"],
    variant: "A",
    crossSell: ["matrix", "compatibility", "forecast"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  matrix: {
    pageId: "matrix",
    path: "/matriz-del-destino",
    titleRu: "Matriz del destino",
    trafficTypes: ["seo", "cold"],
    funnelRole: "first-purchase",
    primaryGoal: "matrix_preview_view",
    secondaryGoals: ["matrix_calc_start", "matrix_cta_click"],
    variant: "A",
    crossSell: ["compatibility", "forecast", "money"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  compatibility: {
    pageId: "compatibility",
    path: "/compatibilidad",
    titleRu: "Compatibilidad",
    trafficTypes: ["cold", "retarget"],
    funnelRole: "first-purchase",
    primaryGoal: "compat_calc_start",
    secondaryGoals: ["compat_preview_view", "compat_cta_click"],
    variant: "A",
    crossSell: ["matrix", "forecast"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  forecast: {
    pageId: "forecast",
    path: "/pronostico-anual",
    titleRu: "Pronóstico anual",
    trafficTypes: ["seo", "retarget"],
    funnelRole: "subscription",
    primaryGoal: "forecast_preview_view",
    secondaryGoals: ["forecast_cta_click"],
    variant: "A",
    crossSell: ["personal_year", "personal_day", "matrix"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  money: {
    pageId: "money",
    path: "/dinero-y-carrera",
    titleRu: "Dinero y carrera",
    trafficTypes: ["cold", "niche"],
    funnelRole: "subscription",
    primaryGoal: "money_preview_view",
    secondaryGoals: ["money_cta_click"],
    variant: "A",
    crossSell: ["matrix", "forecast", "natalnaya-karta-online"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  relocation: {
    pageId: "relocation",
    path: "/mudanza-por-fecha-de-nacimiento",
    titleRu: "Mudanza y geolocalización",
    trafficTypes: ["cold", "niche"],
    funnelRole: "first-purchase",
    primaryGoal: "reloc_preview_view",
    secondaryGoals: ["reloc_cta_click"],
    variant: "A",
    crossSell: ["matrix", "money", "natalnaya-karta-online"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  natal: {
    pageId: "natal",
    path: "/carta-natal",
    titleRu: "Carta natal",
    trafficTypes: ["seo", "cold"],
    funnelRole: "first-purchase",
    primaryGoal: "natal_preview_view",
    secondaryGoals: ["natal_cta_click"],
    variant: "A",
    crossSell: ["matrix", "forecast"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  personal_year: {
    pageId: "personal_year",
    path: "/anio-personal",
    titleRu: "Año personal",
    trafficTypes: ["seo", "cold"],
    funnelRole: "subscription",
    primaryGoal: "personal_year_preview_view",
    secondaryGoals: ["personal_year_cta_click"],
    variant: "A",
    crossSell: ["forecast", "matrix"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  personal_day: {
    pageId: "personal_day",
    path: "/dia-personal",
    titleRu: "Día personal",
    trafficTypes: ["seo", "retarget"],
    funnelRole: "retention",
    primaryGoal: "personal_day_preview_view",
    secondaryGoals: ["personal_day_cta_click"],
    variant: "A",
    crossSell: ["personal_year", "forecast"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  personal_period: {
    pageId: "personal_period",
    path: "/periodo-personal",
    titleRu: "Periodo personal",
    trafficTypes: ["seo", "niche"],
    funnelRole: "first-purchase",
    primaryGoal: "personal_period_preview_view",
    secondaryGoals: ["personal_period_cta_click"],
    variant: "A",
    crossSell: ["personal_year", "matrix"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  lunar: {
    pageId: "lunar",
    path: "/calendario-lunar",
    titleRu: "Calendario lunar",
    trafficTypes: ["seo", "warm"],
    funnelRole: "retention",
    primaryGoal: "lunar_preview_view",
    secondaryGoals: ["lunar_cta_click"],
    variant: "A",
    crossSell: ["personal_day", "forecast", "matrix"],
    showPreview: false,
    showRegistrationGate: true,
    sourceAware: true,
  },
  pricing: {
    pageId: "pricing",
    path: "/pricing",
    titleRu: "Planes",
    trafficTypes: ["warm", "retarget"],
    funnelRole: "subscription",
    primaryGoal: "pricing_cta_click",
    secondaryGoals: ["pricing_plan_hover"],
    variant: "A",
    crossSell: [],
    showPreview: false,
    showRegistrationGate: false,
    sourceAware: true,
  },
  plus_tour: {
    pageId: "plus_tour",
    path: "/pricing/plus-tour",
    titleRu: "Tour por Plus",
    trafficTypes: ["warm", "retarget"],
    funnelRole: "subscription",
    primaryGoal: "plus_tour_cta_click",
    secondaryGoals: ["plus_tour_nav", "plus_tour_scroll_50"],
    variant: "A",
    crossSell: ["pricing", "landing"],
    showPreview: false,
    showRegistrationGate: false,
    sourceAware: false,
  },
  blog: {
    pageId: "blog",
    path: "/blog",
    titleRu: "Blog",
    trafficTypes: ["seo", "warm"],
    funnelRole: "retention",
    primaryGoal: "blog_post_read",
    secondaryGoals: ["blog_cta_click"],
    variant: "A",
    crossSell: ["landing", "matrix"],
    showPreview: false,
    showRegistrationGate: false,
    sourceAware: false,
  },
  faq: {
    pageId: "faq",
    path: "/faq",
    titleRu: "Preguntas frecuentes",
    trafficTypes: ["warm"],
    funnelRole: "objection",
    primaryGoal: "faq_cta_click",
    secondaryGoals: ["faq_expand"],
    variant: "A",
    crossSell: ["pricing", "landing"],
    showPreview: false,
    showRegistrationGate: false,
    sourceAware: false,
  },
  children: {
    pageId: "children",
    path: "/matriz-del-destino-infantil",
    titleRu: "Matriz infantil",
    trafficTypes: ["seo", "niche"],
    funnelRole: "first-purchase",
    primaryGoal: "children_cta_click",
    secondaryGoals: ["children_scroll_50"],
    variant: "A",
    crossSell: ["matritsa-sudby", "prognoz-na-god", "sovmestimost-po-date-rozhdeniya"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  "matritsa-sudby": {
    pageId: "matritsa-sudby",
    path: "/matriz-del-destino",
    titleRu: "Matriz del destino online",
    trafficTypes: ["seo", "cold"],
    funnelRole: "first-purchase",
    primaryGoal: "matritsa_sudby_cta_click",
    secondaryGoals: ["matritsa_sudby_scroll_50", "matritsa_sudby_example_cta"],
    variant: "A",
    crossSell: ["compatibility", "forecast", "natalnaya-karta-online"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  "sovmestimost-po-date-rozhdeniya": {
    pageId: "sovmestimost-po-date-rozhdeniya",
    path: "/compatibilidad",
    titleRu: "Compatibilidad por fecha de nacimiento",
    trafficTypes: ["seo", "cold"],
    funnelRole: "first-purchase",
    primaryGoal: "sovmestimost_cta_click",
    secondaryGoals: ["sovmestimost_scroll_50", "sovmestimost_example_cta"],
    variant: "A",
    crossSell: ["matritsa-sudby", "natalnaya-karta-online", "pereezd-po-date-rozhdeniya"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  "natalnaya-karta-online": {
    pageId: "natalnaya-karta-online",
    path: "/carta-natal",
    titleRu: "Carta natal online",
    trafficTypes: ["seo", "cold"],
    funnelRole: "first-purchase",
    primaryGoal: "natalnaya_karta_cta_click",
    secondaryGoals: ["natalnaya_karta_scroll_50", "natalnaya_karta_example_cta"],
    variant: "A",
    crossSell: ["matritsa-sudby", "prognoz-na-god", "pereezd-po-date-rozhdeniya"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  "prognoz-na-god": {
    pageId: "prognoz-na-god",
    path: "/pronostico-anual",
    titleRu: "Pronóstico anual por fecha de nacimiento",
    trafficTypes: ["seo", "cold"],
    funnelRole: "subscription",
    primaryGoal: "prognoz_na_god_cta_click",
    secondaryGoals: ["prognoz_na_god_scroll_50", "prognoz_example_cta"],
    variant: "A",
    crossSell: ["matritsa-sudby", "natalnaya-karta-online", "sovmestimost-po-date-rozhdeniya"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  "detskaya-matritsa": {
    pageId: "detskaya-matritsa",
    path: "/matriz-del-destino-infantil",
    titleRu: "Matriz del destino infantil",
    trafficTypes: ["seo", "niche"],
    funnelRole: "first-purchase",
    primaryGoal: "detskaya_matritsa_cta_click",
    secondaryGoals: ["detskaya_matritsa_scroll_50", "detskaya_example_cta"],
    variant: "A",
    crossSell: ["matritsa-sudby", "prognoz-na-god"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  "pereezd-po-date-rozhdeniya": {
    pageId: "pereezd-po-date-rozhdeniya",
    path: "/mudanza-por-fecha-de-nacimiento",
    titleRu: "Mudanza por fecha de nacimiento",
    trafficTypes: ["seo", "niche"],
    funnelRole: "first-purchase",
    primaryGoal: "pereezd_cta_click",
    secondaryGoals: ["pereezd_scroll_50", "pereezd_example_cta"],
    variant: "A",
    crossSell: ["matritsa-sudby", "natalnaya-karta-online", "children"],
    showPreview: true,
    showRegistrationGate: true,
    sourceAware: true,
  },
  sonnik: {
    pageId: "sonnik",
    path: "/diccionario-de-suenos",
    titleRu: "Diccionario de sueños online",
    trafficTypes: ["seo", "cold"],
    funnelRole: "first-purchase",
    primaryGoal: "sonnik_cta_click",
    secondaryGoals: ["sonnik_scroll_50"],
    variant: "A",
    crossSell: ["children", "pereezd-po-date-rozhdeniya", "natalnaya-karta-online"],
    showPreview: false,
    showRegistrationGate: true,
    sourceAware: false,
  },
};

export function getPageConfig(pageId: string): PageConfig | null {
  return PUBLIC_PAGES[pageId] ?? null;
}

export function getPageConfigByPath(path: string): PageConfig | null {
  const normalized = path === "/" ? "/" : path.replace(/\/$/, "");
  return Object.values(PUBLIC_PAGES).find((p) => p.path === normalized) ?? null;
}

/** Known UTM-source values and their mapped traffic-type */
export const SOURCE_MAP: Record<string, TrafficType> = {
  yandex_direct: "cold",
  yandex_ads: "cold",
  tg_ads: "cold",
  tg_channel: "warm",
  vk_ads: "cold",
  retarget_ya: "retarget",
  retarget_vk: "retarget",
  email: "warm",
  push: "warm",
  organic: "seo",
  seo: "seo",
  direct: "warm",
};
